<template>
  <div class="login-container">
    <!-- 关闭按钮：固定在屏幕右上角 -->
    <div class="close-btn">
      <el-icon @click="handleClose" class="close-icon">
        <Close />
      </el-icon>
    </div>
    
    <!-- 登录表单方块：居中显示 -->
    <div class="login-box">
      <div class="logo">
        <img src="@/static/logo.png" alt="子沐研究" />
      </div>
      <!-- 登录表单：添加loading状态，防止重复提交 -->
      <el-form  ref="loginFormRef" :model="form"  :rules="rules" label-width="80px" :disabled="isLoading">
        <el-form-item label="手机号" prop="tel">
          <el-input v-model="form.tel" placeholder="请输入手机号" maxlength="11" @input="formatTel"/>
        </el-form-item>
        <el-form-item label="密码" prop="passwd">
          <el-input v-model="form.passwd"  type="password"  placeholder="请输入密码"  show-password/>
        </el-form-item>
        
        <!-- 验证码 -->
        <el-form-item label="验证码" prop="captcha">
          <div class="captcha-box">
            <el-input v-model="form.captcha" placeholder="请输入验证码" maxlength="4"/>
            <SIdentify 
              :identifyCode="identifyCode" 
              @click="refreshCaptcha"
            />
          </div>
        </el-form-item>
        
        <!-- 记住密码 -->
        <el-form-item>
          <el-checkbox v-model="rememberMe">记住用户名和密码</el-checkbox>
        </el-form-item>
        
        <!-- 按钮组：水平对齐 -->
        <el-form-item class="button-group">
          <el-button type="primary"  @click="handleLogin" class="login-btn" :loading="isLoading"> 登录 </el-button>
          <el-button type="default" @click="handleRegister" class="register-btn"> 注册 </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
// 引入api/index中定义的登录接口
import { userLoginApi } from '@/api/index';
import { useUserStore } from '@/store/userStore.js'; 
import SIdentify from '@/components/SIdentify.vue';

let ipcRenderer = null;
try {
  if (window.Electron && window.Electron.ipcRenderer) {
    ipcRenderer = window.Electron.ipcRenderer;
  } else {
    console.warn('window.Electron 未定义，非Electron环境');
  }
} catch (e) {
  console.error('获取ipcRenderer失败：', e);
}

// cookie操作工具函数
const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/`;
};

// const getCookie = (name) => {
//   const nameEQ = `${name}=`;
//   const ca = document.cookie.split(';');
//   for (let i = 0; i < ca.length; i++) {
//     let c = ca[i];
//     while (c.charAt(0) === ' ') c = c.substring(1);
//     if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
//   }
//   return null;
// };

export default {
  components: {
    SIdentify
  },
  setup() {
    const router = useRouter();
    const loginFormRef = ref(null);
    const userStore = useUserStore(); // 实例化userStore

    const form = reactive({
      tel: '', 
      passwd: '',
      captcha: ''
    });
    const rememberMe = ref(false); // 新增响应式变量
    //加载状态：防止重复点击登录
    const isLoading = ref(false);
    const identifyCode = ref('');

    // 生成随机验证码
    const makeCode = (length = 4) => {
      const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
      let code = '';
      for (let i = 0; i < length; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return code;
    };

    // 验证码校验
    const validateCaptcha = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请输入验证码'));
      } else if (value.toLowerCase() !== identifyCode.value.toLowerCase()) {
        callback(new Error('验证码错误'));
      } else {
        callback();
      }
    };

    //表单验证规则：手机号格式+密码必填
    const rules = reactive({
      tel: [
        { required: true, message: '请输入手机号', trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
      ],
      passwd: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, max: 20, message: '密码长度需在6-20位之间', trigger: 'blur' }
      ],
      captcha: [
        { required: true, validator: validateCaptcha, trigger: 'blur' }
      ]
    });

    //手机号输入格式化：自动去除非数字字符（可选优化）
    const formatTel = () => {
      form.tel = form.tel.replace(/\D/g, ''); // 只保留数字
    };

    // 刷新验证码
    const refreshCaptcha = () => {
      identifyCode.value = makeCode(4);
      form.captcha = '';
    };

    // 核心登录逻辑：对接接口+Token存储
    const handleLogin = async () => {
      //表单验证
      const validateResult = await loginFormRef.value.validate().catch(err => {
        console.error('表单验证失败：', err);
        return false;
      });
      if (!validateResult) return;

      //发起登录请求
      isLoading.value = true;
      try {
        // 调用api/index中定义的登录接口（GET请求，参数tel/passwd）
        const data = await userLoginApi.userLogin({
          tel: form.tel,
          passwd: form.passwd
        });

        //处理接口返回(根据接口文档判断成功/失败)
        const expiresDays = rememberMe.value ? 30 : 1; // 30天或会话级
        setCookie('HTTP_USER_TOKEN', data.HTTP_USER_TOKEN, expiresDays);


        // 登录成功:存储Token和用户信息到localStorage
        localStorage.setItem('HTTP_USER_TOKEN', data.HTTP_USER_TOKEN); // 存储Token(接口返回的身份标识)
        localStorage.setItem('userInfo', JSON.stringify({ // 存储用户基本信息
          userId: data.user_id,
          userName: data.user_name,
          tel: data.tel,
          invitationCode: data.invitation_code
        }));
        localStorage.setItem('isLoggedIn', 'true'); // 标记登录状态

        userStore.loginSuccess({
          userId: data.user_id,
          userName: data.user_name,
          avatars: data.head_url,
          token: data.HTTP_USER_TOKEN,
          tel: data.tel
        });

        // 如果勾选了记住密码,保存到localStorage
        if (rememberMe.value) {
          localStorage.setItem('savedUsername', form.tel);
          localStorage.setItem('savedPassword', form.passwd);
          localStorage.setItem('rememberMe', 'true');
        } else {
          // 如果没勾选,清除之前保存的
          localStorage.removeItem('savedUsername');
          localStorage.removeItem('savedPassword');
          localStorage.removeItem('rememberMe');
        }

        // 调用频次记录接口
        try {
          await userLoginApi.userActive({
            user_id: data.user_id
          });
        } catch (err) {
          console.error('频次记录失败：', err);
        }

        // 调用权益激活接口(登录页静默处理,不弹出提示)
        try {
          await userLoginApi.userOrderTrial({
            user_id: data.user_id
          }, { silentError: true });  // 静默失败,不显示错误提示
        } catch (err) {
          console.error('权益激活失败：', err);
        }

        //登录成功后窗口最大化  
        if (ipcRenderer) {
            ipcRenderer.send('window-maximize');
            // 备用方案：直接调用暴露的方法
            if (window.Electron.windowMaximize) {
              window.Electron.windowMaximize();
            }
        }          
        // 提示成功并跳转
        ElMessage.success('登录成功');
        const redirectPath = localStorage.getItem('redirectPath') || '/home';
        router.push(redirectPath);
        // 清除缓存的重定向路径
        localStorage.removeItem('redirectPath');
      } catch (error) {
        // 网络错误/接口异常处理
        console.error('登录接口请求失败：', error);
        ElMessageBox.alert(
          '登录失败，请检查网络或联系管理员', 
          '错误提示', 
          { type: 'error' }
        );
        // 刷新验证码
        refreshCaptcha();
      } finally {
        // 无论成功/失败，关闭loading
        isLoading.value = false;
      }
    };

    // 6. 注册按钮逻辑（跳转注册页，根据实际路由调整）
    const handleRegister = () => {
      router.push('/register'); // 假设注册页路由为/register
    };

    // Electron关闭窗口核心方法
    const handleClose = () => {
      try {
        if (ipcRenderer) {// 通过ipcRenderer通知主进程关闭窗口
          ipcRenderer.send('close-main-window');
        } else { //直接关闭当前窗口
          window.close();
        }
      } catch (e) {
        ElMessage.warning('关闭失败，请手动关闭应用');
      }
    };

    // 组件挂载时生成验证码并读取保存的用户信息
    onMounted(() => {
      refreshCaptcha();
      
      // 读取保存的用户名和密码
      const savedRemember = localStorage.getItem('rememberMe');
      if (savedRemember === 'true') {
        const savedUsername = localStorage.getItem('savedUsername');
        const savedPassword = localStorage.getItem('savedPassword');
        if (savedUsername && savedPassword) {
          form.tel = savedUsername;
          form.passwd = savedPassword;
          rememberMe.value = true;
        }
      }
    });

    return {
      form,
      rules,
      isLoading,
      loginFormRef,
      identifyCode,
      handleLogin,
      handleRegister,
      formatTel,
      refreshCaptcha,
      rememberMe,
      handleClose 
    };
  }
};
</script>

<style scoped>
/* 全屏容器：铺满整个屏幕 */
.login-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, hsl(240, 7%, 91%) 0%, hwb(270 97% 2%) 100%);
  margin: 0;
  padding: 0;
  overflow: hidden;
}

/* 关闭按钮：固定在屏幕右上角 */
.close-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  cursor: pointer;
  z-index: 1000;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.close-btn:hover {
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

.close-icon {
  font-size: 20px;
  color: #666;
  transition: color 0.2s;
}

.close-btn:hover .close-icon {
  color: #333;
}

/* 登录方块：居中的小卡片 */
.login-box {
  width: 420px;
  max-width: 90vw;
  padding: 40px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  position: relative;
  z-index: 1;
}

.logo {
  text-align: center;
  margin-bottom: 32px;
}

.logo img {
  height: 64px;
  margin-bottom: 12px;
}

.logo h2 {
  font-size: 20px;
  color: #1f2937;
  margin: 0;
}

/* 按钮组：水平对齐+平分宽度 */
.button-group {
  display: flex;
  gap: 16px;
  margin-top: 12px;
}

.login-btn, .register-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
}

/* 验证码容器 */
.captcha-box {
  display: flex;
  gap: 10px;
  align-items: center;
}

.captcha-box :deep(.s-canvas) {
  flex-shrink: 0;
}

.captcha-box :deep(.s-canvas canvas) {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.captcha-box :deep(.s-canvas canvas:hover) {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}

.remember-me {
  margin-left: 80px;
  margin-bottom: 0;
}

/* 适配小屏幕 */
@media (max-width: 480px) {
  .login-box {
    width: 90%;
    padding: 28px 24px;
  }
  
  .close-btn {
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
  }
  
  .close-icon {
    font-size: 18px;
  }
}
</style>